import React, { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: 'general' | 'etudiant' | 'tuteur' | 'paiement' | 'technique';
}

const faqData: FAQItem[] = [
  // Questions générales
  {
    question: "Qu'est-ce que tutormatch-heg ?",
    answer: "tutormatch-heg est une application de mise en relation entre étudiant-es et tuteur-trices de la HEG-Genève (Haute école de gestion de Genève), réalisée par l'AEHEG. Elle permet aux étudiant-es de trouver de l'aide académique auprès d'autres étudiant-es plus expérimenté-es.",
    category: 'general'
  },
  {
    question: "Comment fonctionne le système de matching ?",
    answer: "Notre système de matching analyse votre filière, vos besoins et vos disponibilités pour vous proposer les tuteur-trices les plus adapté-es. Vous pouvez également utiliser les filtres avancés pour affiner votre recherche.",
    category: 'general'
  },
  {
    question: "La plateforme est-elle gratuite ?",
    answer: "L'inscription et la recherche de tuteur-trices sont gratuites. Seules les sessions de tutorat sont payantes, avec des tarifs fixés par chaque tuteur-trice (généralement entre 20-50 CHF/heure). La plateforme ne gère pas les paiements et met seulement en relation les parties.",
    category: 'general'
  },

  // Questions étudiants
  {
    question: "Comment trouver un-e tuteur-trice ?",
    answer: "Utilisez notre page de recherche pour filtrer par filière, matière, prix et disponibilités. Vous pouvez ensuite contacter directement les tuteur-trices qui vous intéressent via notre système de matching.",
    category: 'etudiant'
  },
  {
    question: "Comment se déroule une session de tutorat ?",
    answer: "Après avoir matché avec un-e tuteur-trice, vous convenez ensemble du lieu (présentiel ou en ligne), de l'horaire et du contenu. Le paiement se fait directement entre vous, en espèces ou via TWINT.",
    category: 'etudiant'
  },

  // Questions tuteurs
  {
    question: "Comment devenir tuteur-trice ?",
    answer: "Cliquez sur 'Devenir tuteur-trice', créez votre profil en renseignant vos compétences, disponibilités et tarifs. Votre profil sera vérifié avant d'être publié sur la plateforme.",
    category: 'tuteur'
  },
  {
    question: "Comment fixer mes tarifs ?",
    answer: "Vous êtes libre de fixer vos tarifs. Nous recommandons généralement entre 25 et 50 CHF/h selon votre expérience et la complexité des matières enseignées.",
    category: 'tuteur'
  },
  {
    question: "Comment se déroule le paiement ?",
    answer: "Les paiements se font directement entre vous et l'étudiant-e, en main propre ou via Twint. La plateforme ne traite aucun paiement et ne prélève aucune commission.",
    category: 'tuteur'
  },

  // Questions paiement
  {
    question: "Comment ça marche pour les paiements ?",
    answer: "tutormatch-heg est une plateforme de mise en relation gratuite. Les paiements se font directement entre étudiant-es et tuteur-trices, soit en espèces, soit via Twint. Nous n'intervenons pas dans les transactions financières.",
    category: 'paiement'
  },
  {
    question: "La plateforme est-elle vraiment gratuite ?",
    answer: "Oui, tutormatch-heg est entièrement gratuite. Nous ne prélevons aucune commission sur les sessions de tutorat. Notre mission est de faciliter l'entraide entre étudiant-es HEG.",
    category: 'paiement'
  },

  // Questions techniques
  {
    question: "J'ai oublié mon mot de passe, que faire ?",
    answer: "Cliquez sur 'Mot de passe oublié' sur la page de connexion. Vous recevrez un email avec un lien pour réinitialiser votre mot de passe.",
    category: 'technique'
  },
  {
    question: "La plateforme fonctionne-t-elle sur mobile ?",
    answer: "Oui, notre plateforme est entièrement responsive et fonctionne parfaitement sur tous les appareils (smartphone, tablette, ordinateur).",
    category: 'technique'
  }
];

const categories = {
  general: { name: 'Général', icon: 'fas fa-info-circle', color: 'blue' },
  etudiant: { name: 'Étudiant-es', icon: 'fas fa-user-graduate', color: 'green' },
  tuteur: { name: 'Tuteur-trices', icon: 'fas fa-chalkboard-teacher', color: 'purple' },
  paiement: { name: 'Fonctionnement', icon: 'fas fa-handshake', color: 'orange' },
  technique: { name: 'Technique', icon: 'fas fa-cog', color: 'gray' }
};

export const FAQPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFAQ = faqData.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Questions Fréquentes</h1>
            <p className="text-xl text-gray-600">
              Trouvez rapidement les réponses à vos questions
            </p>
          </div>

          {/* Barre de recherche */}
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher dans les questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              />
              <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
            </div>
          </div>

          {/* Filtres par catégorie */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                Toutes les catégories
              </button>
              {Object.entries(categories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                    selectedCategory === key
                      ? `bg-${cat.color}-600 text-white`
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Liste des FAQ */}
          <div className="space-y-4">
            {filteredFAQ.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-search text-gray-400 text-6xl mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Aucune question trouvée
                </h3>
                <p className="text-gray-500">
                  Essayez de modifier vos filtres ou votre recherche
                </p>
              </div>
            ) : (
              filteredFAQ.map((item, index) => {
                const isOpen = openItems.includes(index);
                const category = categories[item.category];
                
                return (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <button
                      onClick={() => toggleItem(index)}
                      className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg bg-${category.color}-100`}>
                            <i className={`${category.icon} text-${category.color}-600`}></i>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {item.question}
                            </h3>
                            <span className={`text-sm text-${category.color}-600 font-medium`}>
                              {category.name}
                            </span>
                          </div>
                        </div>
                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-gray-400 transition-transform`}></i>
                      </div>
                    </button>
                    
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <div className="pl-12">
                          <p className="text-gray-700 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Section contact */}
          <div className="mt-12 bg-blue-50 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Vous ne trouvez pas votre réponse ?
            </h3>
            <p className="text-gray-600 mb-6">
              Notre équipe support est là pour vous aider
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              <i className="fas fa-envelope"></i>
              Nous contacter
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
